import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { toast } from 'sonner';
import axios from 'axios';
import { getApiErrorMessage, unwrapApiData } from '../../utils/proPageMapper';
import { 
  Rocket, Building2, MapPin, Phone, FileText, Tag, 
  CheckCircle2, ArrowRight, AlertCircle, Crown, Loader2,
  ChevronRight, Shield, CreditCard, Star, ExternalLink
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';

import API from '../../constants/api'
// API base via getApiBase()

const CATEGORIES = [
  "Plomberie", "Électricité", "Peinture", "Maçonnerie", "Menuiserie",
  "Rénovation", "Toiture", "Serrurerie", "Carrelage", "Climatisation",
  "Chauffage", "Isolation", "Jardinage", "Nettoyage", "Déménagement"
];

const STEPS = [
  { id: 1, title: "Informations", description: "Renseignez votre activité" },
  { id: 2, title: "Documents KYC", description: "Téléversez vos justificatifs" },
  { id: 3, title: "Abonnement", description: "Choisissez votre formule" }
];

export default function ProUpgrade() {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [proStatus, setProStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [paymentStatus, setPaymentStatus] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    nom_commercial: '',
    siret: '',
    adresse_pro: '',
    telephone_pro: '',
    description: '',
    categories: [],
    zone_intervention: []
  });
  const [zoneInput, setZoneInput] = useState('');

  useEffect(() => {
    fetchProStatus();
    
    // Check for payment return
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');
    
    if (sessionId && success) {
      pollPaymentStatus(sessionId);
    } else if (cancelled) {
      toast.error('Paiement annulé');
    }
  }, [searchParams]);

  const fetchProStatus = async () => {
    try {
      const response = await axios.get(`${API}/pro/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = unwrapApiData(response);
      setProStatus(data);
      
      // Déterminer l'étape actuelle basée sur le statut
      if (data.pro_status === 'non_demande') {
        setCurrentStep(1);
      } else if (['en_cours', 'kyc_pending'].includes(data.pro_status)) {
        setCurrentStep(2);
      } else if (data.pro_status === 'kyc_validated') {
        setCurrentStep(3);
      }
    } catch (error) {
      console.error('Error fetching pro status:', error);
    }
  };

  // Poll payment status after Stripe redirect
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Vérification du paiement expirée. Veuillez rafraîchir la page.');
      setPaymentStatus('timeout');
      return;
    }

    try {
      setPaymentStatus('checking');
      const response = await axios.get(`${API}/subscriptions/session/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = unwrapApiData(response);

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        toast.success('Paiement réussi ! Votre compte Pro est maintenant actif.');
        await refreshUser();
        // Redirect after success
        setTimeout(() => navigate('/tableau-de-bord'), 2000);
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        toast.error('Session de paiement expirée. Veuillez réessayer.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
    }
  };

  // Initiate Stripe checkout
  const handleCheckout = async (planId) => {
    setCheckoutLoading(planId);
    try {
      const response = await axios.post(`${API}/subscriptions/checkout`, {
        plan: 'PRO',
        success_url: `${window.location.origin}/tableau-de-bord/passer-pro?success=1&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/tableau-de-bord/passer-pro?cancelled=1`,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = unwrapApiData(response);
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la création du paiement'));
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleCategoryToggle = (category) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : prev.categories.length < 5
          ? [...prev.categories, category]
          : prev.categories
    }));
  };

  const handleAddZone = () => {
    if (zoneInput.trim() && !formData.zone_intervention.includes(zoneInput.trim())) {
      setFormData(prev => ({
        ...prev,
        zone_intervention: [...prev.zone_intervention, zoneInput.trim()]
      }));
      setZoneInput('');
    }
  };

  const handleRemoveZone = (zone) => {
    setFormData(prev => ({
      ...prev,
      zone_intervention: prev.zone_intervention.filter(z => z !== zone)
    }));
  };

  const handleSubmitStep1 = async (e) => {
    e.preventDefault();
    
    if (formData.categories.length === 0) {
      toast.error('Sélectionnez au moins une catégorie');
      return;
    }
    if (formData.zone_intervention.length === 0) {
      toast.error('Ajoutez au moins une zone d\'intervention');
      return;
    }
    if (formData.description.length < 50) {
      toast.error('La description doit contenir au moins 50 caractères');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/pro/upgrade`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Passage en Pro initié ! Téléversez maintenant vos documents.');
      await refreshUser();
      await fetchProStatus();
      setCurrentStep(2);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de l\'initiation'));
    } finally {
      setLoading(false);
    }
  };

  // Si déjà Pro actif, rediriger
  if (user?.account_type === 'pro' && user?.pro_status === 'actif') {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-center py-12">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Crown className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Vous êtes déjà Pro !</h1>
          <p className="text-slate-600 mb-6">
            Votre compte professionnel est actif. Profitez de toutes les fonctionnalités Pro.
          </p>
          <Button onClick={() => navigate('/tableau-de-bord')} data-testid="back-to-dashboard">
            Retour au tableau de bord
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Passer en compte Pro</h1>
              <p className="text-slate-600">Développez votre activité sur ProConnect</p>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
                    currentStep > step.id
                      ? 'bg-green-500 text-white'
                      : currentStep === step.id
                        ? 'bg-amber-500 text-white'
                        : 'bg-slate-200 text-slate-500'
                  }`}>
                    {currentStep > step.id ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      step.id
                    )}
                  </div>
                  <div className="ml-3 hidden sm:block">
                    <p className={`font-medium ${currentStep >= step.id ? 'text-slate-900' : 'text-slate-400'}`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-slate-500">{step.description}</p>
                  </div>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-1 mx-4 rounded ${
                    currentStep > step.id ? 'bg-green-500' : 'bg-slate-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Information Form */}
        {currentStep === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-amber-600" />
                Informations professionnelles
              </CardTitle>
              <CardDescription>
                Renseignez les informations de votre activité pour créer votre fiche artisan.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitStep1} className="space-y-6">
                {/* Nom commercial */}
                <div className="space-y-2">
                  <Label htmlFor="nom_commercial">Nom commercial *</Label>
                  <Input
                    id="nom_commercial"
                    value={formData.nom_commercial}
                    onChange={(e) => setFormData({ ...formData, nom_commercial: e.target.value })}
                    placeholder="Ex: Dupont Plomberie"
                    required
                    data-testid="pro-nom-commercial"
                  />
                </div>

                {/* SIRET */}
                <div className="space-y-2">
                  <Label htmlFor="siret">Numéro SIRET (optionnel)</Label>
                  <Input
                    id="siret"
                    value={formData.siret}
                    onChange={(e) => setFormData({ ...formData, siret: e.target.value.replace(/\D/g, '').slice(0, 14) })}
                    placeholder="14 chiffres"
                    maxLength={14}
                    data-testid="pro-siret"
                  />
                  <p className="text-xs text-slate-500">Sera vérifié lors de la validation KYC</p>
                </div>

                {/* Adresse */}
                <div className="space-y-2">
                  <Label htmlFor="adresse_pro">Adresse professionnelle *</Label>
                  <Input
                    id="adresse_pro"
                    value={formData.adresse_pro}
                    onChange={(e) => setFormData({ ...formData, adresse_pro: e.target.value })}
                    placeholder="Adresse complète"
                    required
                    data-testid="pro-adresse"
                  />
                </div>

                {/* Téléphone */}
                <div className="space-y-2">
                  <Label htmlFor="telephone_pro">Téléphone professionnel *</Label>
                  <Input
                    id="telephone_pro"
                    value={formData.telephone_pro}
                    onChange={(e) => setFormData({ ...formData, telephone_pro: e.target.value })}
                    placeholder="06 XX XX XX XX"
                    required
                    data-testid="pro-telephone"
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">Description de votre activité *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Décrivez votre activité, vos services, votre expérience... (minimum 50 caractères)"
                    rows={4}
                    required
                    data-testid="pro-description"
                  />
                  <p className="text-xs text-slate-500">
                    {formData.description.length}/50 caractères minimum
                  </p>
                </div>

                {/* Catégories */}
                <div className="space-y-2">
                  <Label>Catégories de métier * (max 5)</Label>
                  <div className="flex flex-wrap gap-2">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => handleCategoryToggle(cat)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          formData.categories.includes(cat)
                            ? 'bg-amber-500 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                        data-testid={`pro-category-${cat.toLowerCase()}`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500">
                    {formData.categories.length}/5 catégories sélectionnées
                  </p>
                </div>

                {/* Zone d'intervention */}
                <div className="space-y-2">
                  <Label>Zone d'intervention *</Label>
                  <div className="flex gap-2">
                    <Input
                      value={zoneInput}
                      onChange={(e) => setZoneInput(e.target.value)}
                      placeholder="Ex: Paris, Île-de-France..."
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddZone())}
                      data-testid="pro-zone-input"
                    />
                    <Button type="button" variant="outline" onClick={handleAddZone}>
                      Ajouter
                    </Button>
                  </div>
                  {formData.zone_intervention.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.zone_intervention.map((zone) => (
                        <span
                          key={zone}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                        >
                          <MapPin className="w-3 h-3" />
                          {zone}
                          <button
                            type="button"
                            onClick={() => handleRemoveZone(zone)}
                            className="ml-1 hover:text-blue-900"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Submit */}
                <div className="pt-4 border-t">
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                    disabled={loading}
                    data-testid="pro-submit-step1"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ArrowRight className="w-4 h-4 mr-2" />
                    )}
                    Continuer vers les documents KYC
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: KYC Documents */}
        {currentStep === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600" />
                Documents de vérification (KYC)
              </CardTitle>
              <CardDescription>
                Pour valider votre compte Pro, nous avons besoin de vérifier votre identité et votre activité.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Info box */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Documents requis</p>
                      <ul className="mt-2 space-y-1 text-sm text-amber-700">
                        <li>• Pièce d'identité (carte d'identité ou passeport)</li>
                        <li>• Extrait Kbis ou carte d'artisan (moins de 3 mois)</li>
                        <li>• Attestation d'assurance RC Pro (optionnel mais recommandé)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Status */}
                {proStatus?.kyc && (
                  <div className={`p-4 rounded-lg border ${
                    proStatus.kyc.status === 'en_attente' 
                      ? 'bg-blue-50 border-blue-200' 
                      : proStatus.kyc.status === 'en_revision'
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-slate-50 border-slate-200'
                  }`}>
                    <p className="font-medium">
                      Statut: {
                        proStatus.kyc.status === 'en_attente' ? '⏳ En attente de validation' :
                        proStatus.kyc.status === 'en_revision' ? '📝 Révision demandée' :
                        proStatus.kyc.status === 'non_soumis' ? '📄 Documents à soumettre' :
                        proStatus.kyc.status
                      }
                    </p>
                    {proStatus.kyc.revision_requests?.length > 0 && (
                      <div className="mt-2 p-3 lux-dashboard-card-sm">
                        <p className="text-sm font-medium text-amber-800">Message de l'équipe:</p>
                        <p className="text-sm text-amber-700">{proStatus.kyc.revision_requests[proStatus.kyc.revision_requests.length - 1]}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Upload section - redirect to KYC page */}
                <div className="text-center py-6">
                  <Button
                    onClick={() => navigate('/tableau-de-bord/kyc')}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="go-to-kyc"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Gérer mes documents KYC
                  </Button>
                </div>

                {/* Next step info */}
                {proStatus?.kyc?.status === 'en_attente' && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
                    <p className="text-blue-800">
                      Vos documents sont en cours de vérification. Vous recevrez une notification dès la validation.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Subscription */}
        {currentStep === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-green-600" />
                Choisissez votre abonnement
              </CardTitle>
              <CardDescription>
                Félicitations ! Votre KYC a été validé. Choisissez maintenant votre formule d'abonnement.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Payment status message */}
                {paymentStatus === 'checking' && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                      <p className="text-blue-800">Vérification du paiement en cours...</p>
                    </div>
                  </div>
                )}
                
                {paymentStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-green-800">Paiement réussi !</p>
                        <p className="text-sm text-green-700">Votre compte Pro est maintenant actif. Redirection...</p>
                      </div>
                    </div>
                  </div>
                )}

                {!paymentStatus && (
                  <>
                    {/* Success message */}
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-green-800">KYC Validé !</p>
                          <p className="text-sm text-green-700">Votre identité a été vérifiée avec succès.</p>
                        </div>
                      </div>
                    </div>

                    {/* Subscription plans with Stripe integration */}
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Monthly Plan */}
                      <div className="lux-dashboard-card p-6 hover:border-amber-500/60 hover:shadow-card">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-bold text-lg">Pro Mensuel</h3>
                            <p className="text-slate-500 text-sm">Flexibilité maximale</p>
                          </div>
                          <Star className="w-5 h-5 text-slate-400" />
                        </div>
                        <div className="mb-4">
                          <span className="text-3xl font-bold">29€</span>
                          <span className="text-slate-500">/mois</span>
                        </div>
                        <ul className="space-y-2 text-sm mb-6">
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            Fiche artisan visible
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            Répondre aux demandes
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            Publier des offres d'emploi
                          </li>
                        </ul>
                        <Button 
                          className="w-full" 
                          variant="outline"
                          onClick={() => handleCheckout('pro_monthly')}
                          disabled={checkoutLoading !== null}
                          data-testid="checkout-monthly"
                        >
                          {checkoutLoading === 'pro_monthly' ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <ExternalLink className="w-4 h-4 mr-2" />
                          )}
                          Souscrire
                        </Button>
                      </div>

                      {/* Annual Plan */}
                      <div className="border-2 border-amber-500 rounded-xl p-6 relative bg-amber-50/50">
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-500 text-white text-xs font-bold rounded-full">
                          POPULAIRE
                        </div>
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-bold text-lg">Pro Annuel</h3>
                            <p className="text-slate-500 text-sm">2 mois offerts</p>
                          </div>
                          <Crown className="w-5 h-5 text-amber-500" />
                        </div>
                        <div className="mb-4">
                          <span className="text-3xl font-bold">290€</span>
                          <span className="text-slate-500">/an</span>
                          <span className="ml-2 text-sm text-green-600 font-medium">-17%</span>
                        </div>
                        <ul className="space-y-2 text-sm mb-6">
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            Tout le plan mensuel
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            Badge "Pro vérifié"
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            Profil mis en avant
                          </li>
                        </ul>
                        <Button 
                          className="w-full bg-amber-500 hover:bg-amber-600"
                          onClick={() => handleCheckout('pro_annual')}
                          disabled={checkoutLoading !== null}
                          data-testid="checkout-annual"
                        >
                          {checkoutLoading === 'pro_annual' ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <ExternalLink className="w-4 h-4 mr-2" />
                          )}
                          Souscrire
                        </Button>
                      </div>
                    </div>

                    <p className="text-center text-slate-500 text-sm">
                      Paiement sécurisé par Stripe. Vous pouvez annuler à tout moment.
                    </p>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Benefits section */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <div className="p-4 lux-dashboard-card">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-semibold mb-1">Visibilité maximale</h3>
            <p className="text-sm text-slate-600">Votre fiche artisan visible par des milliers de clients potentiels.</p>
          </div>
          <div className="p-4 lux-dashboard-card">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
              <FileText className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="font-semibold mb-1">Répondre aux demandes</h3>
            <p className="text-sm text-slate-600">Accédez aux demandes de travaux et proposez vos services.</p>
          </div>
          <div className="p-4 lux-dashboard-card">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
              <Shield className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="font-semibold mb-1">Badge vérifié</h3>
            <p className="text-sm text-slate-600">Gagnez la confiance avec le badge "Pro vérifié".</p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
